import React, { useMemo, useState } from 'react';
import { connect } from "react-redux";
import moment from 'moment';
import Alert from "./glass/GlassAlert";
import Loader from '../components/spinner/spinner-view';
import { creditInvoice } from '../reducers/invoices';

const { calculateInvoiceTotals, format } = require('../helpers/invoiceNumber');

/**
 * Raise a credit note against an invoice.
 *
 * A credit note is a separate document, never an edit of the invoice: it keeps
 * positive amounts and is marked isCreditNote server-side, so VAT computes the
 * same way an invoice's does and only the balance screens negate it. Full
 * credit reverses every line; partial credit reverses chosen lines, optionally
 * at a reduced quantity.
 */
const CreditNoteComponent = ({ invoice, creditInvoice, setShowCredit }) => {
    const lines = useMemo(() => (invoice && invoice.descriptions) || [], [invoice]);

    const [mode, setMode] = useState('full');
    const [picks, setPicks] = useState(() =>
        lines.map((l) => ({ checked: true, qty: l.qty !== undefined ? parseFloat(l.qty) : 1 }))
    );
    const [reason, setReason] = useState('');
    const [creditDate, setCreditDate] = useState(moment().format('YYYY-MM-DD'));
    const [saving, setSaving] = useState(false);

    const isFull = mode === 'full';

    // The lines actually being credited, in the shape the invoice totals helper
    // and the server both expect.
    const creditLines = useMemo(() => {
        return lines
            .map((l, i) => {
                const pick = picks[i] || { checked: true, qty: 1 };
                const checked = isFull ? true : !!pick.checked;
                if (!checked) return null;
                const originalQty = l.qty !== undefined ? parseFloat(l.qty) : 1;
                const qty = isFull ? originalQty : pick.qty;
                if (!qty) return null;
                return { ...l, qty };
            })
            .filter(Boolean);
    }, [lines, picks, isFull]);

    // Discount is deliberately not carried across: a partial credit cannot
    // apportion an invoice-level discount sensibly, so the credited lines are
    // priced as invoiced and the user adjusts a quantity if they need less.
    const totals = useMemo(() => {
        try {
            return calculateInvoiceTotals(creditLines, null);
        } catch (e) {
            return { total: 0, preTotal: 0, tax: 0, tax2: 0 };
        }
    }, [creditLines]);

    const toggle = (i) => {
        if (isFull) return;
        setPicks((prev) => prev.map((p, idx) => idx === i ? { ...p, checked: !p.checked } : p));
    };

    const setQty = (i, raw) => {
        if (isFull) return;
        const max = lines[i].qty !== undefined ? parseFloat(lines[i].qty) : 1;
        const parsed = parseFloat(raw);
        const next = isNaN(parsed) ? 0 : Math.max(0, Math.min(max, parsed));
        setPicks((prev) => prev.map((p, idx) => idx === i ? { ...p, qty: next } : p));
    };

    const submit = () => {
        if (!creditLines.length) {
            Alert.fire({ title: 'Pick at least one line to credit', icon: 'warning' });
            return;
        }
        setSaving(true);
        creditInvoice(
            invoice._id,
            {
                // A full credit sends no lines, so the server credits the
                // invoice as it stands rather than a snapshot taken here.
                descriptions: isFull ? undefined : creditLines,
                reason: reason || undefined,
                invoiceDate: creditDate
            },
            (err, result) => {
                setSaving(false);
                if (err) {
                    Alert.fire({ title: 'Could not create the credit note', text: String(err), icon: 'error' });
                    return;
                }
                Alert.fire({
                    title: 'Credit note created',
                    text: (result && result.creditNoteNumber ? result.creditNoteNumber : 'Credit note') +
                        ' raised against ' + (invoice.invoiceNumber || 'this invoice') + '.',
                    icon: 'success'
                });
                if (setShowCredit) setShowCredit(false);
            }
        );
    };

    const pill = (on) => ({
        height: 32, padding: '0 18px', borderRadius: 999, border: 'none',
        background: on ? 'var(--primary, #0DA1B2)' : 'transparent',
        color: on ? '#fff' : 'var(--text-secondary, #5A6B7A)',
        fontWeight: on ? 700 : 600, fontSize: 13, cursor: 'pointer'
    });

    const th = {
        textAlign: 'left', padding: '12px 10px 8px', fontSize: 10, fontWeight: 700,
        letterSpacing: 0.7, textTransform: 'uppercase', color: 'var(--text-secondary, #5A6B7A)'
    };

    const balanceBefore = (() => {
        try {
            const t = calculateInvoiceTotals(lines, invoice && invoice.discount).total || 0;
            return t - (parseFloat(invoice && invoice.amountPaid) || 0);
        } catch (e) { return 0; }
    })();

    return (
        <div>
            <Loader showSpinner={saving} />

            {/* Scope */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{
                    display: 'inline-flex', background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(255,255,255,0.65)', borderRadius: 999, padding: 4, gap: 4
                }}>
                    <button type="button" style={pill(isFull)} onClick={() => setMode('full')}>Full credit</button>
                    <button type="button" style={pill(!isFull)} onClick={() => setMode('partial')}>Partial credit</button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary, #5A6B7A)' }}>
                    {isFull
                        ? 'Reverses the whole invoice. Every line is credited in full.'
                        : 'Pick the lines to reverse, and reduce a quantity to credit part of a line.'}
                </div>
            </div>

            {/* Header facts */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: '1 1 200px', background: 'rgba(255,255,255,0.6)', border: '1px solid var(--border-light, #E5ECEF)', borderRadius: 14, padding: '12px 16px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: 'var(--text-secondary, #5A6B7A)' }}>Credit of</div>
                    <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>{invoice && invoice.invoiceNumber}</div>
                </div>
                <div style={{ flex: '1 1 200px', background: 'rgba(255,255,255,0.6)', border: '1px solid var(--border-light, #E5ECEF)', borderRadius: 14, padding: '12px 16px' }}>
                    <label htmlFor="cn-date" style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: 'var(--text-secondary, #5A6B7A)', display: 'block', marginBottom: 4 }}>Credit note date</label>
                    <input
                        id="cn-date"
                        type="date"
                        value={creditDate}
                        onChange={(e) => setCreditDate(e.target.value)}
                        style={{ width: '100%', height: 32, border: '1px solid var(--border, #D5E0E5)', borderRadius: 8, padding: '0 10px', fontSize: 14, fontWeight: 600 }}
                    />
                </div>
            </div>

            {/* Lines */}
            <div style={{ border: '1px solid var(--border-light, #E5ECEF)', borderRadius: 14, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th scope="col" style={{ ...th, width: 54, paddingLeft: 16 }}>Credit</th>
                            <th scope="col" style={th}>Description</th>
                            <th scope="col" style={{ ...th, width: 110, textAlign: 'right' }}>Invoiced</th>
                            <th scope="col" style={{ ...th, width: 130, textAlign: 'right' }}>Qty to credit</th>
                            <th scope="col" style={{ ...th, width: 120, textAlign: 'right' }}>Unit price</th>
                            <th scope="col" style={{ ...th, width: 140, textAlign: 'right', paddingRight: 16 }}>Credit amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((l, i) => {
                            const pick = picks[i] || { checked: true, qty: 1 };
                            const checked = isFull ? true : !!pick.checked;
                            const originalQty = l.qty !== undefined ? parseFloat(l.qty) : 1;
                            const qty = isFull ? originalQty : pick.qty;
                            const amount = checked ? qty * parseFloat(l.price || 0) : 0;
                            const dim = !checked;

                            return (
                                <tr key={l.id || l.code || i} style={{ borderTop: '1px solid var(--border-light, #E5ECEF)', background: dim ? 'rgba(238,244,246,0.45)' : 'transparent' }}>
                                    <td style={{ padding: '12px 10px 12px 16px' }}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={isFull}
                                            onChange={() => toggle(i)}
                                            aria-label={'Credit ' + (l.description || 'line ' + (i + 1))}
                                            style={{ width: 17, height: 17, accentColor: 'var(--primary, #0DA1B2)', cursor: isFull ? 'default' : 'pointer' }}
                                        />
                                    </td>
                                    <td style={{ padding: '12px 10px' }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? 'var(--text-muted, #9AA5B4)' : 'var(--text, #0F1F2E)' }}>{l.description}</div>
                                        {l.code && <div style={{ fontSize: 11, color: 'var(--text-muted, #9AA5B4)', marginTop: 2 }}>{l.code}</div>}
                                    </td>
                                    <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: 13, color: 'var(--text-secondary, #5A6B7A)' }}>{originalQty}</td>
                                    <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max={originalQty}
                                            value={qty}
                                            disabled={isFull || !checked}
                                            onChange={(e) => setQty(i, e.target.value)}
                                            aria-label={'Quantity to credit for ' + (l.description || 'line ' + (i + 1))}
                                            style={{
                                                width: 86, height: 30, textAlign: 'right', borderRadius: 8, padding: '0 10px',
                                                border: '1px solid ' + (isFull || !checked ? 'var(--border-light, #E5ECEF)' : 'var(--border, #D5E0E5)'),
                                                background: isFull || !checked ? '#F4F8F9' : '#fff',
                                                color: isFull || !checked ? 'var(--text-muted, #9AA5B4)' : 'var(--text, #0F1F2E)',
                                                fontSize: 13, fontWeight: 600
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: 13, color: 'var(--text-secondary, #5A6B7A)' }}>R {format(parseFloat(l.price || 0))}</td>
                                    <td style={{ padding: '12px 16px 12px 10px', textAlign: 'right', fontSize: 15, fontWeight: 700, color: dim ? 'var(--text-muted, #9AA5B4)' : '#B45309' }}>R {format(amount)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Reason + totals */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <label htmlFor="cn-reason" style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: 'var(--text-secondary, #5A6B7A)', display: 'block' }}>Reason for credit</label>
                    <textarea
                        id="cn-reason"
                        rows="4"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Two handsets returned 02/10 — credited at invoiced price"
                        style={{ marginTop: 8, width: '100%', border: '1px solid var(--border, #D5E0E5)', borderRadius: 10, padding: '10px 12px', fontSize: 13, lineHeight: '20px', resize: 'vertical' }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-muted, #9AA5B4)', marginTop: 6 }}>Printed on the credit note and stored against the document.</div>
                </div>

                <div style={{ flex: '0 0 300px', background: 'rgba(255,255,255,0.7)', border: '1px solid var(--border-light, #E5ECEF)', borderRadius: 14, padding: '16px 18px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: 'var(--text-secondary, #5A6B7A)' }}>Credit total</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-secondary, #5A6B7A)' }}>Subtotal</span>
                        <span style={{ fontWeight: 600 }}>R {format(totals.preTotal || 0)}</span>
                    </div>
                    {!!totals.tax && <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-secondary, #5A6B7A)' }}>Tax</span>
                        <span style={{ fontWeight: 600 }}>R {format(totals.tax || 0)}</span>
                    </div>}
                    {!!totals.tax2 && <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-secondary, #5A6B7A)' }}>Second tax</span>
                        <span style={{ fontWeight: 600 }}>R {format(totals.tax2 || 0)}</span>
                    </div>}
                    <div style={{ height: 1, background: 'var(--border, #D5E0E5)', margin: '12px 0' }}></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>Total credited</span>
                        <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: '#B45309' }}>R {format(totals.total || 0)}</span>
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--border, #D5E0E5)', fontSize: 12, color: 'var(--text-secondary, #5A6B7A)', lineHeight: '18px' }}>
                        Outstanding on this invoice goes from <strong>R {format(balanceBefore)}</strong> to <strong>R {format(balanceBefore - (totals.total || 0))}</strong>.
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCredit && setShowCredit(false)}>Cancel</button>
                <button
                    type="button"
                    onClick={submit}
                    disabled={saving || !creditLines.length}
                    style={{
                        height: 38, padding: '0 20px', borderRadius: 10, border: 'none',
                        background: creditLines.length ? '#B45309' : '#D5E0E5',
                        color: '#fff', fontWeight: 700, fontSize: 13,
                        cursor: creditLines.length ? 'pointer' : 'not-allowed'
                    }}
                >
                    {saving ? 'Creating…' : 'Create credit note'}
                </button>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    invoice: state.invoices.invoice,
    error: state.invoices.error
});

const mapDispatchToProps = {
    creditInvoice: (id, payload, callback) => creditInvoice(id, payload, callback)
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditNoteComponent);
